import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Camera, Sparkles, Upload, Scan } from 'lucide-react';

const VirtualTryOn = () => {
  const [image, setImage] = useState(null);
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const startScanning = () => {
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pt-32 pb-24 px-6" data-testid="virtual-tryon-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles size={20} className="text-[#C27054]" />
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#7A8A73]">
                AI Powered Technology
              </p>
            </div>
            <h1 className="text-4xl lg:text-5xl font-serif tracking-tight text-[#2D3B26] leading-[1.1] mb-4">
              Virtual Try-On
            </h1>
            <p className="text-base text-[#475740] leading-relaxed max-w-2xl mx-auto">
              Experience the future of jewelry shopping. Upload your photo or use your camera to see how our pieces look on you.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Try-On Area */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-black"
            data-testid="tryon-canvas"
          >
            {image ? (
              <>
                <img src={image} alt="Your photo" className="w-full h-full object-cover" />
                {scanning && (
                  <motion.div
                    initial={{ top: 0 }}
                    animate={{ top: '100%' }}
                    transition={{ duration: 3, ease: 'linear' }}
                    className="absolute left-0 right-0 h-1 bg-[#C27054] shadow-[0_0_20px_rgba(194,112,84,0.8)]"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D3B26]/80 to-transparent flex flex-col justify-end p-8">
                  <div className="bg-white/20 backdrop-blur-md rounded-2xl p-6">
                    <p className="text-white text-sm font-sans mb-2">Ready to try jewelry!</p>
                    <p className="text-[#E4E8DF] text-xs">
                      Select a product from our collection to see it on you
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1743264385411-57c883bdc0ea)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D3B26]/80 to-transparent" />
                <div className="relative z-10 text-center text-white p-8">
                  <Scan size={48} className="mx-auto mb-4" />
                  <p className="text-lg font-serif mb-2">Upload Your Photo</p>
                  <p className="text-sm text-[#E4E8DF]">to start your virtual try-on experience</p>
                </div>
              </div>
            )}

            {/* Scanning Frame Overlay */}
            {scanning && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-8 border-2 border-[#C27054] rounded-2xl"
              >
                <div className="absolute top-0 left-0 w-4 h-4 border-t-4 border-l-4 border-[#C27054]" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-4 border-r-4 border-[#C27054]" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-4 border-l-4 border-[#C27054]" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-4 border-r-4 border-[#C27054]" />
              </motion.div>
            )}
          </motion.div>

          {/* Controls */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col justify-center space-y-6"
          >
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-2xl font-serif text-[#2D3B26] mb-6">Get Started</h2>

              <div className="space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="file-input"
                />

                <button
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-photo-button"
                  className="w-full bg-[#C27054] text-white rounded-full px-8 py-4 hover:bg-[#A85B42] transition-all duration-300 shadow-[0_4px_14px_0_rgba(194,112,84,0.39)] hover:-translate-y-0.5 text-sm uppercase tracking-widest font-semibold flex items-center justify-center gap-2"
                >
                  <Upload size={18} />
                  Upload Photo
                </button>

                {image && (
                  <button
                    onClick={startScanning}
                    disabled={scanning}
                    data-testid="scan-face-button"
                    className="w-full bg-[#E4E8DF] text-[#2D3B26] rounded-full px-8 py-4 hover:bg-[#C27054] hover:text-white transition-all duration-300 text-sm uppercase tracking-widest font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Camera size={18} />
                    {scanning ? 'Scanning...' : 'Scan Face'}
                  </button>
                )}
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="bg-[#E4E8DF] rounded-2xl p-8">
              <h3 className="text-lg font-serif text-[#2D3B26] mb-4">How It Works</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-[#C27054] rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold">
                    1
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#2D3B26]">Upload Your Photo</p>
                    <p className="text-xs text-[#7A8A73] mt-1">
                      Choose a clear front-facing photo for best results
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-[#C27054] rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold">
                    2
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#2D3B26]">AI Face Scanning</p>
                    <p className="text-xs text-[#7A8A73] mt-1">
                      Our AI detects your facial features for accurate placement
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-[#C27054] rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold">
                    3
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#2D3B26]">Try Jewelry</p>
                    <p className="text-xs text-[#7A8A73] mt-1">
                      Browse products and see them on you instantly
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="bg-[#FDFBF7] border border-[rgba(45,59,38,0.1)] rounded-2xl p-6">
              <div className="flex gap-3">
                <Sparkles size={20} className="text-[#C27054] flex-shrink-0" />
                <p className="text-xs text-[#7A8A73] leading-relaxed">
                  This is a preview of our AI Virtual Try-On feature. Full integration with product catalog coming soon. Your photos are never stored or shared.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default VirtualTryOn;